import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ClipboardCheck, 
  TrendingUp, 
  Users, 
  Building2,
  ArrowRight,
  CheckCircle,
  Smartphone,
  Globe,
  Shield
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const Home = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: ClipboardCheck,
      title: t('featuresOverview.operations.title'),
      description: t('featuresOverview.operations.description'),
      items: t('featuresOverview.operations.items'),
    },
    {
      icon: TrendingUp,
      title: t('featuresOverview.finance.title'),
      description: t('featuresOverview.finance.description'),
      items: t('featuresOverview.finance.items'),
    },
    {
      icon: Users,
      title: t('featuresOverview.employees.title'),
      description: t('featuresOverview.employees.description'),
      items: t('featuresOverview.employees.items'),
    },
    {
      icon: Building2,
      title: t('featuresOverview.multiBranch.title'),
      description: t('featuresOverview.multiBranch.description'),
      items: t('featuresOverview.multiBranch.items'),
    },
  ];

  const testimonials = t('testimonials.items');

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-32 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#7876bb]/5 via-white to-[#7876bb]/10" />
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#7876bb]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#7876bb]/5 rounded-full blur-2xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <p className="text-[#7876bb] font-medium mb-4 text-lg">
                {t('hero.tagline')}
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                {t('hero.title')}
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-[#7876bb] rounded-xl hover:bg-[#6665a8] transition-all shadow-lg shadow-[#7876bb]/30 hover:shadow-xl hover:shadow-[#7876bb]/40"
                >
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/features"
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-[#7876bb] bg-white border-2 border-[#7876bb]/20 rounded-xl hover:border-[#7876bb]/40 hover:bg-[#7876bb]/5 transition-all"
                >
                  {t('hero.secondaryCta')}
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1571342174342-8f276883484b?w=800&q=80"
                  alt="Restaurant management"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#7876bb]/20 to-transparent" />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 hidden lg:block">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">HACCP Compliant</p>
                    <p className="text-xs text-gray-500">100% Digital</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center space-x-2 text-gray-600">
              <Shield className="w-5 h-5 text-[#7876bb]" />
              <span className="text-sm font-medium">GDPR Compliant</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <Smartphone className="w-5 h-5 text-[#7876bb]" />
              <span className="text-sm font-medium">PWA Ready</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <Globe className="w-5 h-5 text-[#7876bb]" />
              <span className="text-sm font-medium">NL / EN / DE</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <Building2 className="w-5 h-5 text-[#7876bb]" />
              <span className="text-sm font-medium">Multi-Branch</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('featuresOverview.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('featuresOverview.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#7876bb]/30 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-[#7876bb]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#7876bb] transition-colors">
                  <feature.icon className="w-7 h-7 text-[#7876bb] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {Array.isArray(feature.items) && feature.items.map((item, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-500">
                      <CheckCircle className="w-4 h-4 text-[#7876bb] mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('testimonials.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {Array.isArray(testimonials) && testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.role}</p>
                  <p className="text-sm text-[#7876bb]">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-[#7876bb] to-[#5c5a9e] rounded-3xl p-12 md:p-16 text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('cta.title')}
            </h2>
            <p className="text-xl text-white/80 mb-8">
              {t('cta.subtitle')}
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-[#7876bb] bg-white rounded-xl hover:bg-gray-100 transition-colors shadow-lg"
            >
              {t('cta.button')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;